import React, {MutableRefObject, useRef, useState, useEffect} from 'react';
import DragAndDrop from '../dragAndDrop/dragAndDrop';
import Loader from '../loader/loader';

export interface IFileUploaderProps {
  /**
   * Define full width in the parent for component
   */
  fullWidth?: boolean;
  /**
   * Define the size for button
   */
  small?: boolean;
  /**
   * Define if button is secondary
   */
  secondary?: boolean;
  /**
   * Define if is possible to upload one or more files
   */
  multiple?: boolean;
  /**
   * Define if is drag and drop variable
   */
  dragAndDrop?: boolean;
  /**
   * Define if is disabled
   */
  disabled?: boolean;
  /**
   * Set id for input
   */
  id?: string;
  /**
   * Set label for component
   */
  label?: string;
  /**
   * Set different classes for file uploader component
   */
  className?: string;
  /**
   * Set accept formats for file or files
   */
  formats: string;
  /**
   * Set the info message
   */
  helperText?: string;
  /**
   * Set the drag and drop message
   */
  dragAndDropMsg?: string;
  /**
   * Define the value of file or files in component
   */
  value?: IFile | IFile[];
  /**
   * Function to call when files is changed
   */
  onChange: (files?: File[]) => void;
  /**
   * Defaults to "Add files" if not provided.
   */
  text?: string;
  [others: string]: any;
}

export interface IFile {
  file: File;
  loading?: boolean;
  error?: string;
}

const FileUploader: React.FC<IFileUploaderProps> = (props: IFileUploaderProps) => {
  const {
    small,
    secondary,
    multiple,
    dragAndDrop,
    fullWidth,
    id = 'fileupload',
    label,
    className,
    formats,
    helperText,
    dragAndDropMsg,
    disabled,
    value,
    text = 'Add files',
    onChange,
    ...rest
  } = props;

  const [valueState, setValueState] = useState<IFile | IFile[] | undefined>(value ? value : undefined);
  const fileInputRef: MutableRefObject<any> = useRef(null);

  useEffect(() => {
    if (multiple && (value as IFile[])?.length > 0) {
      setValueState(value as IFile[]);
    } else if (value) {
      setValueState(value as IFile);
    }
  }, [value, multiple]);

  const onChangeInput = async (e: any) => {
    if (e.target.files && e.target.files.length > 0) {
      const _newFiles: File[] = Array.from(e.target.files);
      if (multiple && (valueState as IFile[])?.length > 0) {
        let _newFilesMultiple = (valueState as IFile[]).map((_valSt: IFile) => _valSt.file);
        _newFilesMultiple = _newFilesMultiple.concat(_newFiles);
        onChange(_newFilesMultiple);
      } else {
        onChange(_newFiles);
      }
    }
  };

  const onDeleteFile = (indexFile?: number) => {
    if (multiple && (valueState as IFile[]).length > 0) {
      const _valuesToDelete = (valueState as IFile[]).map((val: IFile) => val.file);
      if (indexFile !== undefined) _valuesToDelete.splice(indexFile, 1);
      onChange(_valuesToDelete);
    } else if (valueState && !multiple) {
      onChange([]);
    }
  };

  const handleDrop = (files: any) => {
    if (files && files.length > 0) {
      const _newFiles: File[] = Array.from(files);
      if (multiple && (valueState as IFile[])?.length > 0) {
        let _newFilesMultiple = (valueState as IFile[]).map((_valSt: IFile) => _valSt.file);
        _newFilesMultiple = _newFilesMultiple.concat(_newFiles);
        onChange(_newFilesMultiple);
      } else {
        onChange(_newFiles);
      }
    }
  };

  const onClickShowLoadFromPc = () => {
    if (fileInputRef && fileInputRef.current) fileInputRef.current.click();
  };

  const onKeyDownDragAndDrop = (e: React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.code === 'Enter' || e.code === 'Space') onClickShowLoadFromPc();
  };

  const renderDragAndDrop = () => {
    return (
      <DragAndDrop
        data-testid={rest && rest['data-testid'] ? `${rest['data-testid']}-drag-and-drop` : ''}
        className="file-uploader-drag-and-drop"
        disabled={disabled}
        handleDrop={handleDrop}
        onClick={onClickShowLoadFromPc}
        tabIndex={0}
        onKeyDown={onKeyDownDragAndDrop}
        role="button"
        aria-label={dragAndDropMsg || 'File uploader'}
      >
        <span>{dragAndDropMsg}</span>
      </DragAndDrop>
    );
  };

  const renderFiles = () => {
    if (multiple && (valueState as IFile[]) && (valueState as IFile[]).length > 0) {
      const _valuesRender = valueState as IFile[];
      return _valuesRender.map(
        (_valueRender: IFile, index: number) =>
          _valueRender && (
            <div className="file-uploader-file-container" key={`file-${index}`}>
              <div className={`file-uploader-file ${_valueRender.error ? 'error' : ''}`}>
                <span className="file-uploader-file-name">{_valueRender.file.name}</span>
                {_valueRender.loading ? (
                  <Loader spinner />
                ) : (
                  <>
                    {_valueRender.error && <span className="material-icons error">error</span>}
                    <button
                      type="button"
                      role="button"
                      data-testid={rest && rest['data-testid'] ? `${rest['data-testid']}-file-${index}-delete-btn` : ''}
                      onClick={() => onDeleteFile(index)}
                      disabled={disabled}
                    >
                      <span className="material-icons">cancel</span>
                    </button>
                  </>
                )}
              </div>
              {_valueRender.error && <small className="mt1">{_valueRender.error}</small>}
            </div>
          )
      );
    }
    return (
      valueState && (
        <div className="file-uploader-file-container">
          <div className={`file-uploader-file${(valueState as IFile).error ? '_error' : ''}`}>
            <span className="file-uploader-file-name">{(valueState as IFile).file.name}</span>{' '}
            {(valueState as IFile).loading ? (
              <Loader spinner />
            ) : (valueState as IFile).error ? (
              <span className="material-icons error">error</span>
            ) : (
              <>
                <button
                  type="button"
                  role="button"
                  data-testid={rest && rest['data-testid'] ? `${rest['data-testid']}-file-${0}-delete-btn` : ''}
                  onClick={() => onDeleteFile()}
                  disabled={disabled}
                >
                  <span className="material-icons">cancel</span>
                </button>
              </>
            )}
          </div>
          {(valueState as IFile).error && <small>{(valueState as IFile).error}</small>}
        </div>
      )
    );
  };

  const _dragAndDropModifier = dragAndDrop ? '_drag-drop' : '';
  const _smallModifier = small ? '_small' : '';
  const _fullWidthModifier = fullWidth ? '_full' : '';
  const _disabledModifier = disabled ? '_disabled' : '';

  return (
    <div
      className={`file-uploader-wrapper${_dragAndDropModifier || _smallModifier}${_fullWidthModifier}${_disabledModifier} ${
        className || ''
      }`}
    >
      {label && (
        <label id={`${id}-label`} className={`caption mb1 ${disabled ? 'disabled' : ''}`} aria-hidden={true}>
          {label}
        </label>
      )}

      {dragAndDrop ? (
        renderDragAndDrop()
      ) : (
        <button
          type="button"
          role="button"
          data-testid={rest && rest['data-testid'] ? `${rest['data-testid']}-upload-btn` : undefined}
          className={`${secondary ? 'button-secondary' : 'button-primary'}${small ? '_small' : '_large'}`}
          onClick={onClickShowLoadFromPc}
          disabled={disabled}
        >
          {text}
          <span className="material-icons right" aria-hidden={true}>
            file_upload
          </span>
        </button>
      )}

      {helperText && <small className={`file-uploader-helper-text ${disabled ? 'disabled' : ''}`}>{helperText}</small>}

      {((!multiple && value) || (multiple && value && (value as IFile[]).length > 0)) && (
        <div className="file-uploader-files-container mt2">{renderFiles()}</div>
      )}
      <input
        aria-hidden={true}
        id={id}
        data-testid={rest && rest['data-testid'] ? `${rest['data-testid']}-input` : undefined}
        ref={fileInputRef}
        type="file"
        onChange={onChangeInput}
        accept={formats}
        disabled={disabled}
        multiple={multiple}
      />
    </div>
  );
};

export default FileUploader;
